import { Textarea, type TextareaProps } from '@mantine/core';
import type { ChangeEvent } from 'react';
import type { Except, Merge, SetRequired } from 'type-fest';
import { useCommonInputProps } from '../../../hooks/useCommonInputProps';
import type { Controlled_ValidatedInputProps } from './types';

export type Controlled_MultilineStringInputProps<N extends boolean> = Merge<
    Except<
        SetRequired<TextareaProps, 'readOnly'>,
        'w' | 'rightSectionWidth' | 'rightSection'
    >,
    Controlled_ValidatedInputProps<N, string> & {
        resize?: false;
    }
>;

export const Controlled_MultilineStringInput = <N extends boolean>({
    nullable,
    value,
    onChange,
    label: _label,
    labelInfo,
    severity,
    helperText,
    endingIcon,
    placeholder: _placeholder,
    styles,
    resize,
    autosize,
    minRows,
    maxRows,
    readOnly,
    nullableInReadOnly,
    ...props
}: Controlled_MultilineStringInputProps<N>) => {
    const { rightSectionWidth, rightSection, inputStyles, label, placeholder } =
        useCommonInputProps({
            endingIcon,
            severity,
            helperText,
            nullable,
            onChange,
            value,
            label: _label,
            labelInfo,
            placeholder: _placeholder,
            readOnly,
            nullableInReadOnly,
        });

    const handleChange =
        onChange ?
            (event: ChangeEvent<HTMLTextAreaElement>) =>
                onChange(event.currentTarget.value)
        :   undefined;

    return (
        <Textarea
            {...props}
            readOnly={readOnly}
            value={value ?? ''}
            resize={resize === false ? undefined : 'vertical'}
            autosize={autosize ?? true}
            minRows={minRows ?? 1}
            maxRows={maxRows ?? 4}
            styles={styles ?? { input: inputStyles }}
            label={label}
            placeholder={placeholder}
            rightSectionWidth={rightSectionWidth}
            rightSection={rightSection}
            onChange={handleChange}
            w={'100%'}
        />
    );
};
